import React, { useEffect, useState, useRef } from 'react';
import styles from './TourAnimatedCursor.module.scss';

interface TourAnimatedCursorProps {
  targetSelector: string;
  isVisible: boolean;
  isWaitingForInteraction: boolean;
  onActionCompleted?: () => void;
}

const TourAnimatedCursor: React.FC<TourAnimatedCursorProps> = ({
  targetSelector,
  isVisible,
  isWaitingForInteraction,
  onActionCompleted
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isActionCompleted, setIsActionCompleted] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  // Função para calcular a posição do cursor baseada no elemento alvo
  const updateCursorPosition = () => {
    if (!targetSelector || !isVisible) return;

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Posicionar o cursor bem à esquerda do elemento
    const x = rect.left + scrollLeft + rect.width / 2 - 80;
    const y = rect.top + scrollTop + rect.height / 2 - 16;

    setPosition({ x, y });
  };

  // Atualizar posição quando o elemento alvo muda ou a janela é redimensionada
  useEffect(() => {
    if (!isVisible) return;

    updateCursorPosition();

    const handleResize = () => updateCursorPosition();
    const handleScroll = () => updateCursorPosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    // Usar MutationObserver para detectar mudanças no DOM
    const observer = new MutationObserver(() => {
      animationFrameRef.current = requestAnimationFrame(updateCursorPosition);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetSelector, isVisible]);

  // Detectar quando a ação foi completada
  useEffect(() => {
    if (!isWaitingForInteraction && !isActionCompleted) {
      setIsActionCompleted(true);
      onActionCompleted?.();
      
      // Reset após um tempo
      setTimeout(() => {
        setIsActionCompleted(false);
      }, 1000);
    }
  }, [isWaitingForInteraction, isActionCompleted, onActionCompleted]);

  if (!isVisible) return null;

  return (
    <div
      ref={cursorRef}
      className={`${styles.tourCursor} ${isActionCompleted ? styles.completed : ''} ${isWaitingForInteraction ? styles.waiting : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {/* Ponto central dourado */}
      <div className={styles.cursorCenter}>
        <div className={styles.centerDot}></div>
      </div>

      {/* Efeito de clique */}
      <div className={styles.clickEffect}>
        <div className={styles.ripple}></div>
        <div className={styles.ripple}></div>
        <div className={styles.ripple}></div>
      </div>

      {/* Partículas de sucesso */}
      {isActionCompleted && (
        <div className={styles.successParticles}>
          <div className={styles.particle}></div>
          <div className={styles.particle}></div>
          <div className={styles.particle}></div>
          <div className={styles.particle}></div>
        </div>
      )}

      {/* Texto de instrução */}
      {isWaitingForInteraction && (
        <div className={styles.instructionText}>
          Clique aqui!
        </div>
      )}
    </div>
  );
};

export default TourAnimatedCursor;
